import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { rid } from "../../utils/shipinhao/utils";
import { ShipinhaoGetLocationParamsSchema } from "./schema";

export type ShipinhaoGetLocationParams = z.infer<typeof ShipinhaoGetLocationParamsSchema>;
export { ShipinhaoGetLocationParamsSchema } from "./schema";

export interface Location {
    uid: string;
    name: string;
    longitude: number;
    latitude: number;
    address: string;
    province: string;
    city: string;
    region: string;
    fullAddress: string;
    source: number;
    poiCheckSum: string;
}
export interface Address {
    city: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    poiCheckSum: string;
    province: string;
    region: string;
}

export interface ShipinhaoLocationResponse {
    errCode: number;
    errMsg: string;
    data?: {
        list?: Location[];
        address?: Address;
        total?: number;
    };
}

export type ShipinhaoGetLocationAction = CommonAction<
    ShipinhaoGetLocationParams,
    {
        locations: Location[];
        total: number;
        address?: Address | undefined;
    }
>;

export const shipinhaoGetLocation: ShipinhaoGetLocationAction = async (_task, params) => {
    console.log("视频号获取位置参数", JSON.stringify(params));
    // 验证 cookies
    if (!params.cookies || params.cookies.length === 0) {
        return response(414, "视频号获取位置失败：缺少必需的 cookies 参数", { locations: [], total: 0 });
    }

    // 验证 extraParam 是否存在
    if (!params.extraParam) {
        return response(400, "缺少 extraParam 参数", { locations: [], total: 0 });
    }

    if (!params.extraParam.fingerPrintDeviceId || !params.extraParam.aId || !params.extraParam.uin) {
        return response(400, "fingerPrintDeviceId、aId和uin不能为空", { locations: [], total: 0 });
    }

    const headers = {
        cookie: params.cookies.map((it: { name: string; value: string }) => `${it.name}=${it.value}`).join(";"),
        referer: "https://channels.weixin.qq.com/",
        origin: "https://channels.weixin.qq.com/",
        "user-agent":
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
        "finger-print-device-id": params.extraParam.fingerPrintDeviceId,
        "x-wechat-uin": params.extraParam.uin,
    };

    console.log("视频号获取位置 headers", JSON.stringify(headers));
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];
    const http = new Http(...args);

    const keyword = params.keyword || "";
    const GetTimeStamp = ($: number) => ($ ? Date.now().toString().substring(0, $) : Date.now().toString());
    // 视频号助手位置搜索参数
    const locationParams: Record<string, string | number> = {
        query: keyword,
        cookies: "",
        longitude: 0,
        latitude: 0,
        timestamp: GetTimeStamp(13),
        scene: 7,
        reqScene: 7,
        _log_finder_id: "",
    };

    // 如果有经纬度，添加到参数中
    if (params.latitude && params.longitude) {
        locationParams.latitude = params.latitude;
        locationParams.longitude = params.longitude;
    }

    // 如果有城市，添加到参数中
    if (params.city) {
        locationParams.city = params.city;
    }

    _task.logger.info(
        `视频号获取位置参数: ${locationParams.query}, ${locationParams.latitude}, ${locationParams.longitude}, ${locationParams.city}`,
    );

    const queryData = {
        _aid: params.extraParam?.aId || "",
        _rid: rid(),
        _pageUrl: "https://channels.weixin.qq.com/micro/content/post/finderNewLifeCreate",
    };
    const queryString = new URLSearchParams(queryData).toString();
    console.log(
        "视频号获取位置请求",
        `https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/helper/helper_search_location?${queryString}`,
    );
    // 调用视频号助手位置搜索API
    const res = await http.api<ShipinhaoLocationResponse>(
        {
            method: "post",
            url: `https://channels.weixin.qq.com/micro/content/cgi-bin/mmfinderassistant-bin/helper/helper_search_location?${queryString}`,
            data: locationParams,
            headers: {
                ...headers,
                "Content-Type": "application/json",
            },
        },
        { retries: 3, retryDelay: 20, timeout: 5000 },
    );
    console.log("视频号获取位置响应", JSON.stringify(res));
    _task.logger.info("视频号获取位置响应:", { ...res });

    const isSuccess = res.errCode === 0;
    const message = `视频号获取位置${isSuccess ? "成功" : `失败，原因：${res.errMsg || res.errCode || "未知错误"}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;

    let locations: Location[] = [];
    let address: Address | undefined;
    if (isSuccess && res?.data?.list) {
        locations = res.data.list;
        address = res.data?.address;
    }

    const data: { locations: Location[]; total: number; address: Address | undefined } = {
        locations,
        total: res?.data?.total || locations.length,
        address,
    };

    return response(isSuccess ? 0 : 414, message, data);
};
